import { useQuery, type UseInfiniteQueryResult, type UseQueryResult } from "@tanstack/react-query";
import type { ScheduleSkip } from "@shared/types/schedules";
import type { RunFilterValues, RunsPage } from "@/hooks/use-runs";
import { useRunsList } from "@/hooks/use-runs";
import { schedulePath } from "@/hooks/use-schedules";

/** How many skipped firings the History lists: the newest 20, as a page of firings. */
const SCHEDULE_SKIPS_LIMIT = 20;

/**
 * The firings this schedule did not start (2026-09-28): a skip date, a pause, or the previous
 * run still holding the work, from the runner's `schedule.skip` rows (`GET /schedules/:id/skips`,
 * newest first). Read with the History; not polled.
 */
export function useScheduleSkips(scheduleId: string): UseQueryResult<{ items: ScheduleSkip[] }> {
  return useQuery<{ items: ScheduleSkip[] }>({
    queryKey: [`${schedulePath(scheduleId)}/skips?limit=${SCHEDULE_SKIPS_LIMIT}`],
  });
}

/**
 * A schedule's History (Session 11, P3, D4): ONE list read, `GET /runs?scheduleId=&include=
 * connections&limit=20` (not audited; the path is `runsQueryPath`'s, through `useRunsList`), 20
 * firings a page with the list's own cursor. Each row carries every connection's part of the
 * firing, its transform step and the overlap flag, so an open firing costs no request of its own.
 *
 * The rows of the previous schedule are never kept while another loads (no `keepPreviousRows`):
 * a list scoped to one schedule must never show another's firings, even for a moment.
 */

const SCHEDULE_HISTORY_PAGE_SIZE = 20;

function historyFilters(scheduleId: string): RunFilterValues {
  return { scheduleId, include: "connections" };
}

export function useScheduleHistory(
  scheduleId: string,
): UseInfiniteQueryResult<{ pages: RunsPage[]; pageParams: (string | null)[] }, Error> {
  return useRunsList(historyFilters(scheduleId), SCHEDULE_HISTORY_PAGE_SIZE);
}
